import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { ChatMessage as ChatMessageType } from '../../types';
import { ChatMessage } from './ChatMessage';

interface ChatHistoryProps {
  messages: ChatMessageType[];
  loading?: boolean;
  newMessageId?: string | null;
  typingContent?: string;
  typingMessageId?: string | null;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ 
  messages, 
  loading = false, 
  newMessageId = null,
  typingContent = '',
  typingMessageId = null
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
  const lastScrollHeightRef = useRef<number>(0);
  const lastScrollTime = useRef<number>(0);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Handle scroll position to determine if we should auto-scroll
  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is within 100px of bottom, enable auto-scroll
    setShouldAutoScroll(distanceFromBottom < 100);
  };

  // Auto-scroll when new messages arrive (if enabled)
  useEffect(() => {
    if (shouldAutoScroll) {
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll]);

  // Continuous scroll during typewriter effect
  useEffect(() => {
    if (!shouldAutoScroll || !containerRef.current) return;

    const interval = setInterval(() => {
      if (containerRef.current) {
        const currentHeight = containerRef.current.scrollHeight;
        if (currentHeight !== lastScrollHeightRef.current) {
          lastScrollHeightRef.current = currentHeight;
          scrollToBottom('smooth');
        }
      }
    }, 100); // Check every 100ms for height changes

    return () => clearInterval(interval);
  }, [shouldAutoScroll]);

  // Throttled auto-scroll when typing content changes (every 100ms max)
  useEffect(() => {
    if (!typingContent || !shouldAutoScroll) return;
    
    const now = Date.now();
    if (now - lastScrollTime.current < 100) {
      // Too soon, skip this scroll
      return;
    }
    
    lastScrollTime.current = now;
    scrollToBottom('smooth');
  }, [typingContent, shouldAutoScroll]);

  // Scroll to bottom on initial load
  useEffect(() => {
    scrollToBottom('auto');
  }, []);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messages.map((message) => {
        // Check if this message is currently being typed
        const isTypingMessage = message.messageId === typingMessageId;
        const typingDelta = isTypingMessage && typingContent ? typingContent.slice(message.content.length) : '';
        
        return (
          <ChatMessage
            key={message.messageId}
            message={message}
            enableTypewriter={message.messageId === newMessageId}
            typingDelta={typingDelta}
          />
        );
      })}
      
      {loading && (
        <Box display="flex" gap={0.5} py={2}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'bounce 1.4s infinite ease-in-out both',
              animationDelay: '-0.32s',
              '@keyframes bounce': {
                '0%, 80%, 100%': { transform: 'scale(0)' },
                '40%': { transform: 'scale(1)' },
              },
            }}
          />
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'bounce 1.4s infinite ease-in-out both',
              animationDelay: '-0.16s',
            }}
          />
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'bounce 1.4s infinite ease-in-out both',
            }}
          />
        </Box>
      )}
      
      <div ref={messagesEndRef} />
    </Box>
  );
};

